import { Button, Dialog } from "@mui/material";
import cx from "classnames";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { ReactComponent as ArrowUp } from "assets/arrow-up.svg";
import { ReactComponent as DeleteIcon } from "assets/delete-icon.svg";
import { ReactComponent as ArrowDown } from "assets/dropdown-icon.svg";
import { ReactComponent as XMark } from "assets/x-mark.svg";
import ShotContext from "components/Shot/ShotContext";
import { SLAPSHOT_HOME_URL } from "constants/urls";
import { CutoutInProgress } from "models/Cutout";
import { LayerCompact } from "models/Layer";
import { deleteLayer } from "services/LayerService";

import styles from "./TrackingLayer.module.scss";

interface TrackingLayerProps {
  layerGroup: LayerCompact[];
  layerId: string | undefined;
  setSelectedLayer: React.Dispatch<React.SetStateAction<LayerCompact | null>>;
  setCutoutInProgress: React.Dispatch<
    React.SetStateAction<CutoutInProgress | null>
  >;
  setLayers: React.Dispatch<React.SetStateAction<LayerCompact[] | undefined>>;
  handleSingleClick: () => void;
  children: React.ReactNode;
}
const TrackingLayer: React.FC<TrackingLayerProps> = ({
  layerGroup,
  children,
  setSelectedLayer,
  layerId,
  setLayers,
  handleSingleClick,
  setCutoutInProgress,
}) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const { project, selectedShot, service } = useContext(ShotContext);
  const [hovered, setHovered] = useState<boolean>(false);
  const [showLayerDeleteModal, setShowLayerDeleteModal] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const shouldGroup = useMemo(() => {
    return layerGroup.length > 1;
  }, [layerGroup]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !shouldGroup) return;

    const allKids = Array.from(container.children) as HTMLElement[];

    // Reset styles
    allKids.forEach((el, i) => {
      el.style.position = "";
      el.style.top = "";
      el.style.zIndex = "";
      el.style.padding = "";
      el.style.scale = "";
      el.style.width = "100%";
      el.style.boxSizing = "border-box";
      el.style.opacity = "1";

      if (i === 0) {
        const nameEl = el.querySelector(`#layer-name-${layerGroup[0].id}`);
        if (nameEl) nameEl.innerHTML = layerGroup[0].name;
      }
    });

    container.style.height = "";

    if (isExpanded) {
      return;
    }

    const stackSpacing = 8;
    const stackLimit = 3;
    let accumulatedTop = 0;
    let containerHeight = 0;

    allKids.forEach((childEl, idx) => {
      const rect = childEl.getBoundingClientRect();
      const { height } = rect;

      childEl.style.position = "absolute";
      childEl.style.left = "0px";
      childEl.style.top = `${accumulatedTop}px`;
      childEl.style.zIndex =
        idx === 0 ? `${allKids.length}` : `${allKids.length - idx}`;
      childEl.style.padding = "inherit";

      if (idx !== 0) {
        const scaleX = 1 - idx * 0.1;
        const opacity = 1 - idx * 0.2;
        childEl.style.scale = `${scaleX} 1`;
        childEl.style.opacity = `${opacity}`;
      } else {
        const nameEl = childEl.querySelector(`#layer-name-${layerGroup[0].id}`);
        if (nameEl)
          nameEl.innerHTML = `<strong>Keyframe:  </strong>
          ${layerGroup
            .map((l) => l.frame_number)
            .sort((a, b) => a - b)
            .join(", ")}`;
      }

      if (idx < stackLimit) {
        accumulatedTop += stackSpacing;
        containerHeight +=
          idx === 0 ? height + stackSpacing + stackSpacing / 2 : stackSpacing;
      }
    });

    container.style.height = `${containerHeight + stackSpacing}px`;
  }, [isExpanded, children, layerGroup, shouldGroup]);

  if (!shouldGroup) return children;

  return (
    <div style={{ position: "relative" }}>
      <div
        className={cx(styles.container, !isExpanded && styles.collapsed)}
        ref={containerRef}
      >
        {children}
      </div>

      {!isExpanded && (
        <div
          className={styles.overlay}
          onMouseEnter={() => {
            setHovered(true);
          }}
          onMouseLeave={() => {
            setHovered(false);
          }}
          onClick={handleSingleClick}
        >
          {hovered && (
            <div className={styles.hoveredCon}>
              <button
                type="button"
                className={styles.deleteButton}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLayerDeleteModal(true);
                }}
              >
                <DeleteIcon />
              </button>
            </div>
          )}
        </div>
      )}

      <div
        className={styles.expanderContainer}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className={styles.expander}>
          {isExpanded ? (
            <ArrowUp className={styles.arrowIcon} />
          ) : (
            <ArrowDown className={styles.arrowIcon} />
          )}
        </div>
      </div>

      <Dialog
        open={showLayerDeleteModal}
        slotProps={{
          paper: {
            className: "dialog",
          },
        }}
      >
        <div className="modalContainer">
          <div className="modalHeader">
            <h3 className="modalTitle">Delete Layer</h3>
            <button
              onClick={() => {
                setShowLayerDeleteModal(false);
              }}
              type="button"
              className="closeButton"
            >
              <XMark />
            </button>
          </div>
          <div className={styles.confirmationText}>
            <p>
              Are you sure you want to delete the keyframes{" "}
              {layerGroup
                .map((l) => l.frame_number)
                .sort((a, b) => a - b)
                .join(", ")}
              ?
            </p>
          </div>
          <div className="modalActions">
            <Button
              type="button"
              className={styles.noBackgroundButton}
              onClick={() => {
                setShowLayerDeleteModal(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className={styles.outlineButtonRed}
              onClick={(e) => {
                e.stopPropagation();
                const layerIdsToDelete = layerGroup.map((layer) => layer.id);
                Promise.all(layerIdsToDelete.map((id) => deleteLayer(id)))
                  .then(() => {
                    if (
                      layerId &&
                      +layerId === layerGroup[0].id &&
                      selectedShot
                    ) {
                      navigate(
                        `${SLAPSHOT_HOME_URL}projects/${project.id}/${service}/shots/${selectedShot.id}`,
                      );
                    }
                    setLayers((prevLayers) =>
                      prevLayers?.filter(
                        (l) => !layerIdsToDelete.includes(l.id),
                      ),
                    );
                  })
                  .catch((err) => {
                    toast.error(err.data);
                  })
                  .finally(() => {
                    setSelectedLayer(null);
                    setCutoutInProgress(null);
                    setShowLayerDeleteModal(false);
                  });
              }}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default TrackingLayer;
